import { Anchor, Breadcrumbs, Button, Center, Grid, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconChartBar, IconPlus, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import MetricsSelector from '../../components/MetricsSelector';
import Metadata from '../../components/Metadata';
import NPSSurveysCards from '../../components/NPS/NPSSurveysCards';
import { Page } from '../../constants/pageTitles';
import { daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';

const NPSSurveysPage = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const [npsSurveys, setNpsSurveys] = useState();
  const [scores, setScores] = useState({});
  const [error, setError] = useState();
  const [filter, setFilter] = useState('');
  const [startDate, setStartDate] = useState(daysAgoISODate(29));
  const [endDate, setEndDate] = useState(todayISODate());

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/nps-surveys`)
      .then(({ npsSurveys }) => setNpsSurveys(npsSurveys))
      .catch(setError);
  }, [accountId, domainId, fetch]);

  useEffect(() => {
    if (!npsSurveys?.length) return;
    setScores({});
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/nps-surveys/scores?startDate=${startDate}&endDate=${endDate}`
    )
      .then(({ scores: batchScores }) => {
        const allScores = Object.fromEntries(npsSurveys.map((s) => [s.id, batchScores[s.id] ?? null]));
        setScores(allScores);
      })
      .catch(() => setScores(Object.fromEntries(npsSurveys.map((s) => [s.id, null]))));
  }, [accountId, domainId, fetch, npsSurveys, startDate, endDate]);

  const filtered = npsSurveys?.filter((s) =>
    !filter || s.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <Metadata title={Page.npsSurveys} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Text>NPS Surveys</Text>
      </Breadcrumbs>

      {npsSurveys?.length > 0 && (
        <Grid mb="md" align="flex-end">
          <Grid.Col span={{ base: 12, sm: 'content' }}>
            <MetricsSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              scope="npsSurveys"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 'auto' }}>
            <TextInput
              placeholder="Search by name…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 'content' }}>
            <Button
              component="a"
              href={`#/accounts/${accountId}/domains/${domainId}/nps/new`}
              leftSection={<IconPlus size={14} />}
            >
              New NPS Survey
            </Button>
          </Grid.Col>
        </Grid>
      )}

      {error && <ErrorMessage message={error.message} />}
      {!npsSurveys && !error && <Loader />}
      {npsSurveys?.length === 0 ? (
        <Center py={80}>
          <Stack align="center" gap="lg">
            <IconChartBar size={72} stroke={1} color="var(--mantine-color-dimmed)" />
            <Stack align="center" gap="xs">
              <Title order={3}>No NPS surveys yet</Title>
              <Text c="dimmed">Create your first NPS survey to start collecting customer feedback.</Text>
            </Stack>
            <Button
              component="a"
              href={`#/accounts/${accountId}/domains/${domainId}/nps/new`}
              leftSection={<IconPlus size={18} />}
              size="lg"
            >
              New NPS Survey
            </Button>
          </Stack>
        </Center>
      ) : (
        <>
          {filtered && filtered.length > 0 && <NPSSurveysCards npsSurveys={filtered} scores={scores} />}
          {filtered?.length === 0 && (
            <Center py={80}>
              <Stack align="center" gap="md">
                <IconSearch size={48} stroke={1} color="var(--mantine-color-dimmed)" />
                <Stack align="center" gap="xs">
                  <Title order={4}>No NPS surveys found</Title>
                  <Text c="dimmed">Try a different search term.</Text>
                </Stack>
              </Stack>
            </Center>
          )}
        </>
      )}
    </>
  );
};

export default NPSSurveysPage;
